import React, { FC, useEffect, useState } from "react";
import {
  mergeStyleSets,
  mergeStyles,
  DefaultButton,
  PrimaryButton,
  IStyle
} from "@fluentui/react";
import { FontIcon } from "office-ui-fabric-react/lib/Icon";
import { connect } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { State } from "../../../store";
import * as ActivityLogStore from "../../../store/ActivityLogsStore";
import * as GeneralStore from "../../../store/GeneralStore";
import * as SofViewStore from "../../../store/SofViewStore";
import * as TruckWorkPacketStore from "../../../store/TruckWorkPacketStore";
import * as TruckNominationStore from "../../../store/TruckNominationStore";
import * as WorkPacketStore from "../../../store/WorkPacketStore";
import * as LineHandlingStore from "../../../store/LineHandlingStore";

import TruckNominationHeader from "../TruckNominationHeader";
import { toNominationNo } from "../../../services/Helper";
import SofIcon from "../../common/Icons";
import PictureAndUpload from "../common/PictureAndUpload";
import RemarksPane from "../RemarksPane";
import {
  ActivityLog,
  ApiCallModel,
  TruckComment,
  TruckNomination
} from "../../../models";
import NominationForm from "./NominationForm";
import { Get, Post } from "../../../api/apiUtils";
import { commentsApi } from "../../../api/commentApi";
import CommentHistory from "../common/CommentHistory";
import { MODALITY } from "../../../models/WorkPacket";
import { loadPdfDoc } from "../../../services/PdfDocumentService";

type IProps = ActivityLogStore.ActivityLogsState &
  SofViewStore.SofViewState &
  TruckWorkPacketStore.TruckWorkPacketState &
  TruckNominationStore.TruckNominationState &
  WorkPacketStore.WorkPacketState &
  LineHandlingStore.LineHandlingState &
  typeof TruckNominationStore.actionCreators &
  typeof TruckWorkPacketStore.actionCreators &
  typeof ActivityLogStore.actionCreators &
  typeof GeneralStore.actionCreators &
  typeof SofViewStore.actionCreators &
  typeof WorkPacketStore.actionCreators;

type Props = {
  lineHandlingStatus: number;
  isLineHandlingRejected: boolean;
  nomination: TruckNomination | null;
  isEditActivityAllowed: boolean;
  isAddCommentAllowed: boolean;
  isDeleteCommentAllowed: boolean;
  isOffline: boolean;
  isApproved: boolean;
  onNominationEditStart(): void;
  onNominationEditEnd(): void;
  onNominationEdit(nom: TruckNomination): void;
  activityLog: ApiCallModel<ActivityLog[]> | null;
  selTransferType: string;
};

type Prop = IProps & Props;

const panelRow = mergeStyles({
  display: "flex"
});
const headerTitle = mergeStyles({
  color: "#00447E",
  letterSpacing: "0.004em"
});
const buttonBase = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0"
    }
  }
} as IStyle);

const classes = mergeStyleSets({
  headerRow: [
    panelRow,
    {
      alignItems: "center",
      padding: "6px 24px",
      background: "white",
      borderBottom: "1px solid #E2E6E8",
    }
  ],
  headerRowTextPane: [
    panelRow,
    {
      flexDirection: "column",
      flexGrow: 1,
      margin: "0 10px",
      paddingLeft: 10,
      borderLeft: "1px solid #E2E6E8"
    }
  ],
  headerTitle: [
    headerTitle,
    {
      fontSize: 16,
      lineHeight: "20px",
      fontWeight: "bold"
    }
  ],
  viewButton: {
    width: "40px",
    height: "40px",
    borderRadius: "5px",
    border: "1px solid #E2E6E8",
    backgroundColor: "#FAFAFA",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    selectors: {
      ":hover": {
        backgroundColor: "#EEEEEE",
        textDecoration: "none"
      }
    }
  },
  contentTitle: {
    color: "#4C4C4C",
    fontSize: 20,
    fontWeight: 700,
    lineHeight: "24px",
    margin: 0
  },
  leftPane: [
    panelRow,
    {
      flexDirection: "column",
      height: "100%",
      margin: "8px 12px 0",
      overflow: "hidden"
    }
  ],
  buttonRow: {
    border: "1px solid #E2E6E8",
    alignItems: "center",
    display: "flex",
    padding: 10,
    marginTop: "16px"
  },
  button: [
    buttonBase,
    {
      backgroundColor: "#F2F4F6",
      color: "#4C4C4C",
      display: "flex",
      alignItems: "center",
      padding: "9px 15px"
    }
  ]
});

enum NomView {
  Verify = 0,
  Rejected = 1,
  Comments = 3,
  Edit = 4
}

const NominationTruckRejection: FC<Prop> = (props: Prop) => {
  const { nominationNo } = useParams() as any;

  const history = useHistory();
  const { state } = useLocation();

  const [view, setView] = useState<NomView>(NomView.Verify);
  const [files, setFiles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [editReason, setEditReason] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [rejection, setRejectionData] = useState<any>();
  const [rejectionErr, setRejectionErr] = useState(false);
  const [comments, setComments] = useState<TruckComment[]>([]);
  const [modality, setModality] = useState<MODALITY>("TRUCK");

  const [reasonForRejection, setReasonForRejection] = useState("");
  const _modality =
    history.location.pathname.indexOf("marine") >= 0
      ? "marine"
      : history.location.pathname.indexOf("truck") >= 0
      ? "truck"
      : "rail";

  useEffect(() => {
    if (files.length > 0) {
      setFiles(files);
      setSubmit(false);
    }
  }, [files]);

  useEffect(() => {
    const handleData = async () => {
      const data = await getData();

      setRejectionData(data);
      setReasonForRejection(data.issue);

      setFiles(
        data.attachments.map((item: any) => {
          return {
            name: item.fileName,
            fileSize: item.fileSize,
            preview: item.url,
            canDelete: false
          };
        }) ?? []
      );
      // setIsLoading(false);
    };
    handleData();
  }, []);

  useEffect(() => {
    if (history.location.pathname.indexOf("marine") >= 0) {
      setModality("MARINE");
    } else if (history.location.pathname.indexOf("truck") >= 0) {
      setModality("TRUCK");
    } else if (history.location.pathname.indexOf("rail") >= 0) {
      setModality("RAIL");
      getComments();
    }
  }, []);

  useEffect(() => {
    props.getAllWorkPackets();
    props.getWPValues(nominationNo);
  }, []);

  const getData = async () => {
    const response = await Get(`/api/Nomination/${nominationNo}/rejection`);
    const resultRes = await response.json();
    return resultRes;
  };

  const getComments = async () => {
    const comments = await commentsApi.getTruckCommentsAsync(nominationNo);
    setComments(comments);
  };

  const handleSubmitReject = async () => {
    setIsLoading(true);
    setSubmit(true);
    setRejectionErr(false);
    if (reasonForRejection.length !== 0 && files.length !== 0) {
      let data: any[] = [];
      data = files
        .filter((f) => f.canDelete)
        .map((file: any) => {
          delete file.canDelete;
          return {
            fileName: file.name,
            fileSize: file.fileSize,
            content: file.preview,
            issue: reasonForRejection,
            type: file.type
          };
        });
      const confirm = await Post(
        `/api/Nomination/${nominationNo}/rejection/confirm`,
        JSON.stringify(data)
      );
      setIsLoading(false);

      if (confirm.ok) {
        await uploadDocuments();
        const result = await confirm.json();
        setView(NomView.Rejected);
        history.push(`/${modality.toLowerCase()}/${result.nominationNo}/sof`);
      } else {
        setRejectionErr(true);
      }
    }
  };

  const uploadDocuments = async () => {
    try {
      console.log("uploadDocuments invoked.");
      const documents = props.workPacketDocuments.result?.filter(
        (f) => f.workPacketType > 1
      );

      console.log(props.workPacketDocuments);
      console.log(documents);

      documents?.forEach(async (doc) => {
        const workPacket = props.workPackets.result?.filter(
          (f) => f.id === doc.workPacketId
        );

        const pdfFile =
          workPacket === undefined ? "" : workPacket[0].layouts[0].pdfFile;

        const pdfDoc = await loadPdfDoc(
          pdfFile,
          nominationNo,
          doc.nominationNo,
          doc.workPacketType === 3 ? doc.id : undefined,
          props.lineHandling,
          props.workPacketValues
        );
        if (pdfDoc) {
          const base64String = await pdfDoc.saveAsBase64({ dataUri: true });

          const last2 = base64String.slice(-2);
          const fileSize =
            base64String.length * (3 / 4) - (last2 === "==" ? 2 : 1);

          const payload = {
            fileName: doc.name,
            content: base64String,
            fileSize: fileSize
          };
          await Post(
            `/api/Attachment/truck/reports/${nominationNo}/rejected`,
            JSON.stringify(payload)
          );
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const deleteImage = (name: string) => {
    setFiles((files) => files.filter((file) => file.name !== name));
  };

  const cancelRejection = async () => {
    const cancel = await Post(
      `/api/Nomination/${nominationNo}/rejection/cancel`,
      null
    );
    if (cancel.ok) {
      history.goBack();
    }
  };

  return (
    <>
      <div className="WorkPacketListWrapper">
        <div className={classes.headerRow}>
          <SofIcon iconname="Truck2" />
          <div className={classes.headerRowTextPane}>
            <span className={classes.headerTitle}>
              {toNominationNo(nominationNo)}
            </span>
          </div>
        </div>
      </div>

      <TruckNominationHeader {...props} />

      <div
        className="ms-Grid-row"
        style={{
          padding: "12px 12px",
          fontWeight: "bold",
          borderBottom: "1px solid #E2E6E8",
          borderTop: "1px solid #E2E6E8",
          margin: 0,
          color: "#7C7777",
          background: "white"
        }}
      >
        <div id="packet-scroll" dir="ltr">
          <div className="ms-Grid-col ms-md8" style={{ paddingLeft: 0 }}>
            <div
              className="d-flex"
              style={{
                alignItems: "center",
                gap: "18px"
              }}
            >
              <button
                className={classes.viewButton}
                onClick={() => history.goBack()}
              >
                <FontIcon iconName="back" />
              </button>
              <h4 className={classes.contentTitle}>Rejection Details</h4>
            </div>
          </div>
        </div>
      </div>

      <div className={classes.leftPane}>
        <div
          className="d-flex w-100 h-100"
          style={{
            gap: "16px"
          }}
        >
          <div
            style={{
              width: "35%",
              background: "white",
              padding: "16px",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div className="rejection-details-wrapper">
              <div className={`${classes.buttonRow} verify-reject`}>
                <SofIcon
                  iconname="CircleRejectionColored"
                  iconcolor="#D32F2F"
                />
                <h3>
                  {view === NomView.Verify && "Verify Rejection"}
                  {[NomView.Rejected, NomView.Comments, NomView.Edit].includes(
                    view
                  ) && "Rejected"}
                </h3>

                {/* {view === NomView.Rejected && (
                    <div className="d-flex w-100" style={{ justifyContent: "end" }}>
                      <button
                        id="btn-nomination-comment"
                        className={classes.button}
                        onClick={() => setView(NomView.Comments)}
                      >
                        <SofIcon iconname="Comment" />
                      </button>
                      <button
                        id="btn-edit-nomination"
                        className={classes.button}
                        onClick={() => setView(NomView.Edit)}
                        style={{marginLeft: 10}}
                      >
                        <SofIcon iconname="Edit" />
                      </button>
                    </div>
                  )} */}

                {[NomView.Comments, NomView.Edit].includes(view) && (
                  <div
                    className="d-flex w-100"
                    style={{ justifyContent: "end" }}
                  >
                    <button
                      className={classes.button}
                      onClick={() => setView(NomView.Rejected)}
                    >
                      <SofIcon iconname="Back" />
                      &nbsp; Back
                    </button>
                  </div>
                )}
              </div>
              {view === NomView.Verify && (
                <>
                  <table className="rejection tbl-detail">
                    <tbody>
                      <tr>
                        <td>
                          <dt>Customer</dt>
                          <dd id="rej-nomination-customer">
                            {rejection?.customer}
                          </dd>
                        </td>
                        <td>
                          <dt>Customer Order #</dt>
                          <dd id="rej-nomination-order-number">
                            {rejection?.customerOrderNo}
                          </dd>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <dt>Carrier</dt>
                          <dd id="rej-nomination-customer">
                            {rejection?.carrier}
                          </dd>
                        </td>
                        <td>
                          <dt>Tank</dt>
                          <dd id="rej-nomination-order-number">
                            {rejection?.tank}
                          </dd>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2} className={editReason ? "on-edit" : ""}>
                          <dt>Reason for Rejection</dt>
                          <dd id="rej-nomination-reason">
                            {editReason ? (
                              <textarea
                                className={`rej-nomination-reason ${
                                  reasonForRejection.length === 0 && submit
                                    ? "error"
                                    : ""
                                }`}
                                value={reasonForRejection}
                                onChange={(e) => {
                                  setReasonForRejection(e.target.value);
                                }}
                              ></textarea>
                            ) : (
                              <>
                                {reasonForRejection}
                                {reasonForRejection.length === 0 && submit && (
                                  <span
                                    style={{ color: "red", fontWeight: 400 }}
                                  >
                                    Issue is required
                                  </span>
                                )}
                              </>
                            )}
                            {state.rejectionStatus !== 4 &&
                              (props?.truckNomination?.result?.status ?? 0) <
                                2 && (
                                <button
                                  id="btn-edit-nomination"
                                  className={classes.button}
                                  onClick={() => setEditReason(!editReason)}
                                  style={{ marginLeft: 10 }}
                                  disabled={isLoading}
                                >
                                  {editReason ? (
                                    <FontIcon iconName="CheckMark" />
                                  ) : (
                                    <SofIcon iconname="Edit" />
                                  )}
                                </button>
                              )}
                          </dd>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2}>
                          <dt>Operational Step</dt>
                          <dd id="rej-nomination-tank-inspection">
                            {rejection?.operationalStep}
                          </dd>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table
                    style={{ marginTop: 16 }}
                    className="rejection tbl-detail"
                  >
                    <tbody>
                      <tr>
                        <td>
                          <dt>Operator</dt>
                          <dd>{rejection?.operator}</dd>
                        </td>
                        <td>
                          <dt>Supervisor</dt>
                          <dd>{rejection?.supervisor}</dd>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}
              {view === NomView.Rejected && (
                <>
                  <table className="rejection tbl-detail">
                    <tbody>
                      <tr>
                        <td>
                          <dt>Customer</dt>
                          <dd id="rej-nomination-customer">
                            {props?.truckNomination?.result?.customer}
                          </dd>
                        </td>
                        <td>
                          <dt>Customer Order Number</dt>
                          <dd id="rej-nomination-order-number"></dd>
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <dt>Carrier</dt>
                          <dd id="rej-nomination-customer"></dd>
                        </td>
                        <td>
                          <dt>Truck Number</dt>
                          <dd id="rej-nomination-order-number"></dd>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2}>
                          <dt>Reason for Rejection</dt>
                          <dd id="rej-nomination-reason">
                            Trailer has strong odor and stains
                          </dd>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={2}>
                          <dt>Operational Step</dt>
                          <dd id="rej-nomination-order-number">
                            {props?.truckNomination?.result?.currentStep}
                          </dd>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <table
                    style={{ marginTop: 16 }}
                    className="rejection tbl-detail"
                  >
                    <tbody>
                      <tr>
                        <td>
                          <dt>Operator</dt>
                          <dd></dd>
                        </td>
                        <td>
                          <dt>Supervisor</dt>
                          <dd></dd>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </>
              )}

              {view === NomView.Comments && (
                <RemarksPane
                  nominationNo={nominationNo}
                  isAddCommentAllowed={props.isAddCommentAllowed}
                  isDeleteCommentAllowed={props.isDeleteCommentAllowed}
                  isOffline={props.isOffline}
                  readonly={props.isApproved}
                  onClose={() => setView(NomView.Rejected)}
                />
              )}

              {view === NomView.Edit && props.nomination && (
                <NominationForm
                  {...props.nomination}
                  lineHandlingStatus={props.lineHandlingStatus}
                  onSuccess={(result: TruckNomination) => {
                    setView(NomView.Rejected);
                    props.onNominationEdit(result);
                    props.onNominationEditEnd();
                  }}
                  onClose={() => {
                    setView(NomView.Rejected);
                    props.onNominationEditEnd();
                  }}
                />
              )}

              {state.rejectionStatus !== 4 &&
                [NomView.Verify].includes(view) && (
                  <>
                    <div className="rejection-btn-row">
                      <PrimaryButton
                        type="submit"
                        onClick={() => {
                          handleSubmitReject();
                        }}
                        disabled={isLoading}
                      >
                        <SofIcon iconname="CircleRejectionColored" />
                        Confirm Rejection
                      </PrimaryButton>

                      <DefaultButton
                        onClick={() => {
                          cancelRejection();
                        }}
                        disabled={isLoading}
                      >
                        <SofIcon iconname="CloseIcon" />
                        Cancel Rejection
                      </DefaultButton>
                    </div>
                    {rejectionErr ? (
                      <p className="error-upload page error-txt">
                        Please attach image/photo to confirm rejection
                      </p>
                    ) : (
                      ""
                    )}
                  </>
                )}
              {modality === "RAIL" && (
                <div style={{ marginTop: 20 }}>
                  <CommentHistory comments={comments} />
                </div>
              )}
            </div>
          </div>
          <div
            className="modal-rejection page"
            style={{
              width: "65%",
              background: "white",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              position: "relative"
            }}
          >
            <div
              className={`rejection-page-wrapper ${
                files.length === 0 && submit ? "error" : ""
              }`}
            >
              {state.rejectionStatus !== 4 && view === NomView.Verify && (
                <PictureAndUpload
                  {...{
                    displayPreview: false,
                    multipleUpload: true,
                    isUploading: isUploading || isLoading,
                    hasError: hasError,
                    files,
                    setFiles,
                    disabled: isLoading
                  }}
                />
              )}

              {files.length > 0 && (
                <div className="files-grid">
                  {files.map((file: any, index: number) => {
                    return (
                      <div key={index} className="rejected-imgs">
                        {NomView.Rejected !== view && file.canDelete && (
                          <button
                            disabled={isUploading}
                            onClick={() => {
                              deleteImage(file.name);
                            }}
                            className="btn-close"
                          >
                            <FontIcon iconName="cancel" />
                          </button>
                        )}
                        <img alt={file.name} src={file.preview} />
                      </div>
                    );
                  })}
                </div>
              )}
              {files.length === 0 && submit && (
                <p className="error-upload page error-txt">
                  Image or Photo is required
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default connect(
  (state: State) => ({
    ...state.truckWorkPacket,
    ...state.activityLogs,
    ...state.truckNomination,
    ...state.workPacket
  }),
  {
    ...TruckWorkPacketStore.actionCreators,
    ...ActivityLogStore.actionCreators,
    ...GeneralStore.actionCreators,
    ...SofViewStore.actionCreators,
    ...TruckNominationStore.actionCreators,
    ...WorkPacketStore.actionCreators
  }
)(NominationTruckRejection as any);
